import React, { useState, useEffect } from 'react';
import { X, CreditCard, Save, Loader, FileText, Plus, Trash2 } from 'lucide-react';
import { syncManager } from '../../services/sync';

const LogPaymentModal = ({ onClose }) => {
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [users, setUsers] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [selectedUserInvoices, setSelectedUserInvoices] = useState([]);

    const [formData, setFormData] = useState({
        userEmail: '',
        amount: '',
        method: 'EFT / Bank Transfer',
        reference: '',
        notes: '',
        allocations: [] // [{ invoiceId, amount }]
    });

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        if (formData.userEmail) {
            loadUserInvoices(formData.userEmail);
        }
    }, [formData.userEmail]);

    const loadData = async () => {
        try {
            const [rawUsers, rawInvoices] = await Promise.all([
                syncManager.fetchTabData('Users'),
                syncManager.fetchTabData('Invoices_DB')
            ]);
            if (rawUsers) {setUsers(rawUsers);}
            if (rawInvoices) {setInvoices(rawInvoices);}
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const loadUserInvoices = (email) => {
        const userInvoices = invoices.filter(inv =>
            inv.Client_Email === email &&
            inv.Status !== 'Paid' &&
            parseFloat(inv.Balance || inv.Total_Amount || 0) > 0
        );
        setSelectedUserInvoices(userInvoices);
    };

    const addAllocation = () => {
        setFormData(prev => ({
            ...prev,
            allocations: [...prev.allocations, { invoiceId: '', amount: '' }]
        }));
    };

    const updateAllocation = (index, field, value) => {
        const newAllocations = [...formData.allocations];
        newAllocations[index][field] = value;

        // Auto-calculate remaining balance for selected invoice
        if (field === 'invoiceId' && value) {
            const invoice = selectedUserInvoices.find(inv => inv.Invoice_ID === value);
            if (invoice) {
                const balance = parseFloat(invoice.Balance || invoice.Total_Amount || 0);
                newAllocations[index].maxAmount = balance;
            }
        }

        setFormData(prev => ({ ...prev, allocations: newAllocations }));
    };

    const removeAllocation = (index) => {
        setFormData(prev => ({
            ...prev,
            allocations: prev.allocations.filter((_, i) => i !== index)
        }));
    };

    const getTotalAllocated = () => {
        return formData.allocations.reduce((sum, alloc) => sum + parseFloat(alloc.amount || 0), 0);
    };

    const getUnallocated = () => {
        const total = parseFloat(formData.amount || 0);
        return total - getTotalAllocated();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        const totalPayment = parseFloat(formData.amount);
        const totalAllocated = getTotalAllocated();

        if (formData.allocations.length > 0 && Math.abs(totalPayment - totalAllocated) > 0.01) {
            alert(`Payment amount (R${totalPayment.toFixed(2)}) doesn't match allocated amount (R${totalAllocated.toFixed(2)})`);
            return;
        }

        setSubmitting(true);
        try {
            await syncManager.triggerWorkflow('hq-log-payment', {
                ...formData,
                amount: totalPayment
            });
            alert('Payment logged successfully!');
            onClose();
        } catch (error) {
            alert('Error: ' + error.message);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {return <div className="p-10 text-center"><Loader className="animate-spin mx-auto" /></div>;}

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200">
                <div className="bg-slate-50 border-b border-slate-100 p-6 flex justify-between items-center sticky top-0">
                    <div className="flex items-center gap-3">
                        <div className="bg-emerald-100 p-2 rounded-xl text-emerald-600">
                            <CreditCard size={20} />
                        </div>
                        <h2 className="text-xl font-black text-slate-800">Log Manual Payment</h2>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full text-slate-400">
                        <X size={20} />
                    </button>
                </div>
                <div className="p-6">
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="col-span-2">
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">User / Client</label>
                                <select required value={formData.userEmail} onChange={e => setFormData({ ...formData, userEmail: e.target.value, allocations: [] })} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl">
                                    <option value="">-- Choose User --</option>
                                    {users.map(u => <option key={u.Client_Email} value={u.Client_Email}>{u.Client_Name} ({u.Client_Email})</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Total Amount (ZAR)</label>
                                <input required type="number" step="0.01" value={formData.amount} onChange={e => setFormData({ ...formData, amount: e.target.value })} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl" placeholder="0.00" />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Payment Method</label>
                                <select value={formData.method} onChange={e => setFormData({ ...formData, method: e.target.value })} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl">
                                    <option>EFT / Bank Transfer</option>
                                    <option>Cash</option>
                                    <option>PayPal</option>
                                    <option>Payfast (Manual Verification)</option>
                                </select>
                            </div>
                            <div className="col-span-2">
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Bank Reference / Notes</label>
                                <input value={formData.reference} onChange={e => setFormData({ ...formData, reference: e.target.value })} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl" placeholder="e.g. Bank ref: 12345678" />
                            </div>
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4">
                                <div className="flex justify-between items-center mb-3">
                                    <div className="flex items-center gap-2">
                                        <FileText size={16} className="text-blue-600" />
                                        <h3 className="text-sm font-bold text-blue-900">Invoice Allocation (Optional)</h3>
                                    </div>
                                    <button type="button" onClick={addAllocation} className="flex items-center gap-1 text-xs font-bold text-blue-600 hover:text-blue-700">
                                        <Plus size={14} /> Add Invoice
                                    </button>
                                </div>

                                {formData.allocations.map((alloc, idx) => (
                                    <div key={idx} className="bg-white rounded-xl p-3 mb-2 flex gap-2 items-start">
                                        <div className="flex-1">
                                            {selectedUserInvoices.length > 0 ? (
                                                <select value={alloc.invoiceId} onChange={e => updateAllocation(idx, 'invoiceId', e.target.value)} className="w-full p-2 text-sm bg-slate-50 border border-slate-200 rounded-lg mb-2">
                                                    <option value="">-- Select Invoice --</option>
                                                    {selectedUserInvoices.map(inv => (
                                                        <option key={inv.Invoice_ID} value={inv.Invoice_ID}>
                                                            {inv.Invoice_ID} - R{(inv.Balance || inv.Total_Amount || 0).toFixed(2)}
                                                        </option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <input
                                                    type="text"
                                                    value={alloc.manualInvoiceId || ''}
                                                    onChange={e => {
                                                        const newAllocations = [...formData.allocations];
                                                        newAllocations[idx].manualInvoiceId = e.target.value;
                                                        newAllocations[idx].invoiceId = e.target.value;
                                                        setFormData(prev => ({ ...prev, allocations: newAllocations }));
                                                    }}
                                                    className="w-full p-2 text-sm bg-amber-50 border border-amber-200 rounded-lg mb-2"
                                                    placeholder="Enter Invoice ID manually (e.g., INV-001)"
                                                />
                                            )}
                                            <input type="number" step="0.01" value={alloc.amount} onChange={e => updateAllocation(idx, 'amount', e.target.value)} className="w-full p-2 text-sm bg-slate-50 border border-slate-200 rounded-lg" placeholder="Amount to allocate" />
                                            {alloc.maxAmount && <p className="text-xs text-slate-500 mt-1">Max: R{alloc.maxAmount.toFixed(2)}</p>}
                                        </div>
                                        <button type="button" onClick={() => removeAllocation(idx)} className="p-2 text-red-400 hover:text-red-600">
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                ))}

                                {formData.allocations.length > 0 && (
                                    <div className="mt-3 pt-3 border-t border-blue-200">
                                        <div className="flex justify-between text-sm">
                                            <span className="font-bold text-slate-600">Total Allocated:</span>
                                            <span className="font-bold text-emerald-600">R{getTotalAllocated().toFixed(2)}</span>
                                        </div>
                                        {getUnallocated() !== 0 && (
                                            <div className="flex justify-between text-sm mt-1">
                                                <span className="font-bold text-slate-600">Unallocated:</span>
                                                <span className={`font-bold ${getUnallocated() > 0 ? 'text-amber-600' : 'text-red-600'}`}>
                                                    R{getUnallocated().toFixed(2)}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                )}

                                <p className="text-xs text-blue-600 mt-2">💡 Leave empty to record as unallocated payment (general account credit)</p>

                        </div>

                        <button type="submit" disabled={submitting} className="w-full bg-emerald-600 text-white py-4 rounded-xl font-bold uppercase tracking-widest flex items-center justify-center gap-2">
                            {submitting ? <Loader className="animate-spin" /> : <><Save size={20} /> Record Payment</>}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default LogPaymentModal;
